import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ApiError } from '@/lib/api';

/**
 * Layar terakhir sebelum pengguna melihat halaman putih.
 *
 * Tanpa ini React Router memakai layar bawaannya: latar putih polos berisi
 * stack trace berbahasa Inggris. Di lapangan itu terbaca sebagai "aplikasinya
 * rusak dan data saya hilang", padahal biasanya hanya satu komponen yang
 * gagal — dan draf penilaian sudah tersimpan lewat autosave.
 *
 * Sengaja TIDAK menelan galatnya diam-diam: pesan teknisnya tetap ditampilkan
 * di bagian yang bisa dilipat, karena itulah satu-satunya bahan yang bisa
 * dikirim pengguna saat melapor.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  const { title, description } = describe(error);

  return (
    <div className="mx-auto flex min-h-dvh max-w-xl flex-col justify-center gap-4 p-6">
      <Card>
        <div className="mb-3 text-4xl" aria-hidden>
          ⚠️
        </div>
        <h1 className="text-2xl font-semibold text-ink">{title}</h1>
        <p className="mt-2 text-ink-secondary">{description}</p>

        <div className="mt-5 flex flex-wrap gap-2.5">
          {/* Muat ulang lebih dulu: sebagian besar galat render pulih setelah
              state komponen dibuang, dan ini yang paling mudah dipahami. */}
          <Button onClick={() => window.location.reload()}>Muat ulang halaman</Button>
          <Button variant="secondary" onClick={() => navigate('/')}>
            Kembali ke Beranda
          </Button>
        </div>

        <p className="mt-5 text-[13px] text-ink-muted">
          Draf penilaian yang sedang diisi tersimpan otomatis, jadi isian Anda tidak hilang saat
          halaman dimuat ulang.
        </p>

        <details className="mt-4">
          <summary className="cursor-pointer text-[13px] font-medium text-ink-secondary">
            Rincian teknis
          </summary>
          <pre className="mt-2 max-h-56 overflow-auto rounded-[var(--radius-input)] bg-app p-3 text-[12px] whitespace-pre-wrap text-ink-secondary">
            {detail(error)}
          </pre>
        </details>
      </Card>
    </div>
  );
}

function describe(error: unknown): { title: string; description: string } {
  if (isRouteErrorResponse(error) && error.status === 404) {
    return {
      title: 'Halaman tidak ditemukan',
      description: 'Alamat yang Anda tuju tidak ada. Periksa kembali tautannya.',
    };
  }

  if (error instanceof ApiError) {
    // 401 tidak akan sampai ke sini — penanganan sesi kedaluwarsa sudah
    // memindahkan pengguna ke halaman masuk sebelum render gagal.
    if (error.status === 403) {
      return {
        title: 'Tidak punya akses',
        description:
          'Akun Anda tidak berwenang membuka data ini. Hubungi admin SLI bila seharusnya punya akses.',
      };
    }
    return {
      title: 'Server tidak bisa memenuhi permintaan',
      description: error.message,
    };
  }

  return {
    title: 'Terjadi kesalahan',
    description:
      'Ada bagian aplikasi yang gagal ditampilkan. Memuat ulang halaman biasanya sudah cukup.',
  };
}

function detail(error: unknown): string {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return error.stack ?? error.message;
  return String(error);
}
